import {useEffect, useMemo} from 'react';
import {createGetOneHook} from '@/utils/createGetOneHook';
import {createFieldsHook} from '@/utils/createFieldsHook';
import AuditorAuditsApiService from '@/modules/auditorAudits/services/AuditorAuditsApiService';
import {
  CACHE_KEY_AUDITOR_AUDITS,
  CACHE_KEY_LIST_AUDITOR_AUDITS, CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS,
  CACHE_KEY_SECTIONS_MANIFEST,
} from '@/modules/auditorAudits/contants/auditorAudits';
import {auditorAuditsFields, fields} from '@/modules/auditorAudits/contants/auditorAudits.form';
import {useMutation, useQueryClient, useQuery} from 'react-query';
import useAlerts from '@/hooks/useAlerts';
import {useHistory} from 'react-router-dom';
import {CACHE_KEY_AUDITS} from '@/modules/audits/contants/audits';
import {useParams} from 'react-router-dom';
import {useAuditData} from '@/modules/auditorAudits/hooks/useAuditData';
import {createWizardAuditsFieldsHook} from '@/utils/createWizardAuditsFieldsHook';
import ServicesApiService from '@/services/ServicesApiService';
import TemplatesApiService from '@/modules/templates/services/TemplatesApiService';
import {calcScore} from '@/modules/auditorAudits/utils/CalcScore';
import {useAuditCalc} from '@/modules/auditorAudits/hooks/useAuditCalc';
import {useRunAuditOnlineStatus} from '@/contexts/RunAuditOnlineStatusProvider';
import {TYPE_SECTION} from '@/modules/templates/contants/sections';
import moment from 'moment';
import {timeElapsedFunction} from '@/utils/realtimeFromNow';

export const useGetOneAuditorAudits = createGetOneHook(AuditorAuditsApiService, 'id', CACHE_KEY_AUDITOR_AUDITS);
export const useFields = createFieldsHook('auditorAudits', fields);
export const useAuditorAuditsFields = createWizardAuditsFieldsHook('auditorAudits', auditorAuditsFields);


const updateIssuesAndEvidences = ({dataSections, sectionId, questionId, issue, evidences}) => {
  return dataSections?.map(s => {
    if (sectionId === s?._id) {
      return {...s, questions: s?.questions?.map(q => {
        if (q?._id === questionId) {
          return {...q, issues: q?.issues?.map(i => {

            if (i?._id === issue) return {...i, evidences: evidences?.filter(x => x)};
            return {...i};
          })};
        }
        return {...q};
      })};
    }
    return {...s};
  })?.filter(x => x);
};

const updateNoteImages = ({dataSections, sectionId, questionId, noteImages}) => {
  return dataSections?.map(s => {
    if (sectionId === s?._id) {
      return {...s, questions: s?.questions?.map(q => {
        if (q?._id === questionId) {
          return {...q, noteImages};
        }
        return {...q};
      })};
    }
    return {...s};
  })?.filter(x => x);
};

export const useGetAllTemplates = (filters, params) => {
  const queryConfig = useMemo(() => {
    return {enabled: !!(filters)};
  }, [filters]);

  const {data, isLoading} = useQuery(['CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS', filters], async () => {
    const {data} = await TemplatesApiService.search({filters, ...params});
    return data;
  }, queryConfig);
  return {isLoading, data};
};

export const useGetAllServices = (filters, params) => {
  const queryConfig = useMemo(() => {
    return {enabled: !!(filters)};
  }, [filters]);

  const {data, isLoading} = useQuery(['CACHE_KEY_LIST_SERVICES', filters], async () => {
    const {data} = await ServicesApiService.search({filters, ...params});
    return data;
  }, queryConfig);
  return {isLoading, data};
};

/*const updateSection = ({sectionId, dataSections, section}) => {
  return dataSections?.map(s => {
    if (sectionId === s?._id) {
      return {...section};
    }
    return {...s};
  })?.filter(x => x);
};*/

export const useGetSectionsManifest = (auditId) => {
  const queryConfig = useMemo(() => ({ enabled: !!auditId }), [auditId]);
  const { data, isLoading } = useQuery(
    [CACHE_KEY_SECTIONS_MANIFEST, auditId],
    async () => {
      const { data } = await AuditorAuditsApiService.getSectionsManifest({ auditId });
      return data;
    },
    queryConfig
  );
  return { data, isLoading };
};

export const useGetAllSections = (auditId, preview) => {
  const queryConfig = useMemo(() => {
    return {enabled: !!(auditId)};
  }, [auditId]);

  const {isLoading, isError, error, data, isFetching} = useQuery([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, auditId], async () => {
    const {data} = await AuditorAuditsApiService.getAllSections({auditId, preview});
    return data;
  }, queryConfig);

  return {isLoading, isError, error, data, isFetching};
};

export const useCrudRunAudit = () => {
  const queryClient = useQueryClient();
  const {setSaving} = useAuditData();
  const {putErrorCode} = useAlerts();
  const {setSectionScore, setTimeElapsed, setAuditScore} = useAuditCalc();
  const {id} = useParams();
  const {online} = useRunAuditOnlineStatus();
  let auditList;

  const {mutateAsync: create, isLoading: isCreating} = useMutation(AuditorAuditsApiService.addAnswer,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        let prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        const auditData = queryClient.getQueryData([CACHE_KEY_AUDITS, id]);
        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);
        auditList = prevData;
        if (newValue?.templateAnswer?.type !== 'text' && newValue?.templateAnswer?.type !== 'readingRegistry') {
          const scoring = prevData.find((item) =>
            item._id === newValue?.section)?.questions?.find((question) =>
            question._id === newValue?.question)?.select?.scoring;
          if (scoring) {
            const auditQuestionsCalcs = calcScore({newValue, data: prevData, auditData});
            setSectionScore({
              finalScore: auditQuestionsCalcs?.questionFinalScore,
              totalScore: auditQuestionsCalcs?.questionTotalScore,
              id: newValue?.section
            });
            if (prevData?.length > 0) {
              prevData.find((item) => item._id === newValue?.section).finalScore = auditQuestionsCalcs?.questionFinalScore;
              prevData.find((item) => item._id === newValue?.section).totalScore = auditQuestionsCalcs?.questionTotalScore;
            }
            if (auditData.data) {
              setAuditScore({
                totalScore: auditQuestionsCalcs?.auditTotalScore,
                finalScore: auditQuestionsCalcs?.auditFinalScore,
              });
              auditData.data.totalScore = auditQuestionsCalcs?.auditTotalScore;
              auditData.data.finalScore = auditQuestionsCalcs?.auditFinalScore;
            }
          }
        }

        if(newValue && newValue?.templateAnswer && prevData?.length > 0 && newValue?.templateAnswer?.type === 'select'){
          prevData = prevData?.map((item) => {
            if (item._id === newValue?.section) {
              item?.questions?.map((question) => {
                if (question._id === newValue?.question) {
                  if (question?.answer === undefined) {
                    question.answer = {templateAnswer: newValue?.templateAnswer};
                  } else {
                    question.answer.templateAnswer.select = newValue?.templateAnswer?.select;
                  }
                }
                return question;
              });
            }
            return item;
          });
        }
        if (newValue && newValue?.templateAnswer && prevData?.length > 0 && newValue?.templateAnswer?.type === 'readingRegistry') {
          prevData = prevData?.map((item) => {
            if (item._id === newValue?.section) {
              item?.questions?.map((question) => {
                if (question._id === newValue?.question) {
                  if (question?.answer === undefined) {
                    question.answer = {templateAnswer: {readingRegistry : [newValue?.templateAnswer?.readingRegistry]}};
                  } else {
                    const registryIndex = question?.answer?.templateAnswer?.readingRegistry?.findIndex((reg) =>
                      (reg.placesMeasure === newValue?.templateAnswer?.readingRegistry?.placesMeasure
                      && reg.optionKey === newValue?.templateAnswer?.readingRegistry?.optionKey)
                    );
                    if (registryIndex === -1) {
                      question.answer.templateAnswer.readingRegistry.push(newValue?.templateAnswer?.readingRegistry);
                    } else {
                      question.answer.templateAnswer.readingRegistry[registryIndex].value = newValue?.templateAnswer?.readingRegistry?.value;
                    }
                  }
                }
                return question;
              });
            }
            return item;
          });
        }

        if (newValue && newValue?.templateAnswer && prevData?.length > 0 && newValue?.templateAnswer?.type === 'text') {
          const modifiedQuestion = prevData?.find((item) =>
            item._id === newValue?.section).questions.find((question) =>
            question._id === newValue?.question);
          if (modifiedQuestion.answer === undefined) {
            modifiedQuestion.answer = {templateAnswer: newValue?.templateAnswer};
          } else {
            modifiedQuestion.answer.templateAnswer.text = newValue?.templateAnswer?.text;
          }
        }

        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return auditList;
        });
        online && putErrorCode(e);
      }
    });

  const {mutateAsync: update, isLoading: isUpdating} = useMutation(AuditorAuditsApiService.updateAnswer,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        auditList = prevData;
        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        if(newValue && newValue?.templateAnswer && prevData?.length > 0 && newValue?.templateAnswer?.type === 'select'){
          prevData.find((item) =>
            item._id === newValue.section).questions.find((question) =>
            question._id === newValue.question).answer.templateAnswer.select = newValue.templateAnswer.select;
        }

        if(newValue && newValue?.templateAnswer && prevData?.length > 0 && newValue?.templateAnswer?.type === 'readingRegistry'){
          prevData.find((item) =>
            item._id === newValue.section).questions.find((question) =>
            question._id === newValue.question).answer.templateAnswer.readingRegistry.find((reg) =>
            reg.placesMeasure === newValue.templateAnswer.readingRegistry.placesMeasure).value = newValue.templateAnswer.readingRegistry.value;
        }
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return auditList;
        });
        online && putErrorCode(e);
      }
    });

  const {mutateAsync: remove, isLoading: isRemoving} = useMutation(AuditorAuditsApiService.removeAnswer,
    {
      onSuccess: () => {
        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);
        queryClient.invalidateQueries(CACHE_KEY_LIST_AUDITOR_AUDITS);
        queryClient.invalidateQueries(CACHE_KEY_AUDITS);
        queryClient.invalidateQueries([CACHE_KEY_AUDITS, id]);
        queryClient.invalidateQueries(['audit-sections',id]);
      },
      throwOnError: true
    }
  );

  const {mutateAsync: createMetadataValue, isLoading: isMetadataValueCreating} = useMutation(AuditorAuditsApiService.addMetadataValue,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_AUDITS, id]);

        let prevData = queryClient.getQueryData([CACHE_KEY_AUDITS, id]);
        auditList = prevData;

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        if(newValue && (newValue?.coverFields?.value || newValue?.coverFields?.value === '') && (prevData?.data?.coverFields)){
          prevData.data.coverFields = prevData?.data?.coverFields?.map((item) => {
            if (item._id === newValue.coverFields._id) {
              item.value = newValue.coverFields.value;
            }
            return item;
          });
        }
        queryClient.setQueryData([CACHE_KEY_AUDITS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_AUDITOR_AUDITS);
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_AUDITS, id], () => {
          return auditList;
        });
        online && putErrorCode(e);
      }
    });

  const {mutateAsync: updateContact, isLoading: isUpdatingContact} = useMutation(AuditorAuditsApiService.updateContacts,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_AUDITS, id]);

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        let prevData = queryClient.getQueryData([CACHE_KEY_AUDITS, id]);
        auditList = prevData;
        if(newValue && (newValue?.contactFields?.value || newValue?.contactFields?.value === {}) && (prevData?.data?.contactFields || prevData?.length > 0)){
          prevData.data.contactFields = prevData?.data?.contactFields?.map((item) => {
            if (item._id === newValue.contactFields._id) {
              item.value = newValue.contactFields.value;
            }
            return item;
          });
        }
        queryClient.setQueryData([CACHE_KEY_AUDITS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_AUDITS, id], () => {
          return auditList;
        });
        online && putErrorCode(e);
      }
    });

  useEffect(() => setSaving(isCreating || isUpdating || isRemoving || isMetadataValueCreating), [isMetadataValueCreating, isCreating, isRemoving, isUpdating, setSaving]);

  return {
    create,
    isCreating,
    update,
    isUpdating,
    remove,
    isRemoving,
    createMetadataValue,
    isMetadataValueCreating,
    updateContact,
    isLoading: isCreating || isUpdating || isRemoving || isMetadataValueCreating || isUpdatingContact
  };
};

export const useCrudIncidentsRunAudit = () => {
  const queryClient = useQueryClient();
  const {online} = useRunAuditOnlineStatus();
  const {putErrorCode} = useAlerts();
  const {setSaving} = useAuditData();
  const {id} = useParams();
  const {setTimeElapsed} = useAuditCalc();
  let prevIncidenceData;

  const {mutateAsync: create, isLoading: isCreating} = useMutation(AuditorAuditsApiService.addAndRemoveTasks,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        prevIncidenceData = prevData;

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        // On add
        if(newValue && newValue?.add && prevData?.length > 0){
          prevData.find((item) =>
            item._id === newValue.sectionId).questions.find((q) =>
            q._id === newValue.questionId).issues.find((issue) =>
            issue._id === newValue.add[0].issue).selected = true;
          if ( newValue.add[0].task) {
            prevData.find((item) =>
              item._id === newValue.sectionId).questions.find((q) =>
              q._id === newValue.questionId).issues.find((issue) =>
              issue._id === newValue.add[0].issue).tasks.find((task) =>
              task._id === newValue.add[0].task).selected = true;
          }
        }
        // On remove
        if(newValue && newValue?.remove && prevData?.length > 0){
          if ( newValue.remove[0].task ) {
            prevData.find((item) =>
              item._id === newValue.sectionId).questions.find((q) =>
              q._id === newValue.questionId).issues.find((issue) =>
              issue._id === newValue.remove[0].issue).tasks.find((task) =>
              task._id === newValue.remove[0].task).selected = false;
          } else {
            prevData.find((item) =>
              item._id === newValue.sectionId).questions.find((q) =>
              q._id === newValue.questionId).issues.find((issue) =>
              issue._id === newValue.remove[0].issue).selected = false;

            prevData.find((item) =>
              item._id === newValue.sectionId).questions.find((q) =>
              q._id === newValue.questionId).issues.find((issue) =>
              issue._id === newValue.remove[0].issue).tasks.map((task) =>
              task.selected = false);
          }
        }

        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevIncidenceData;
        });
        online && putErrorCode(e);
      }
    });

  const {mutateAsync: updateName, isLoading: isUpdatingName} = useMutation(AuditorAuditsApiService.updateName,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        prevIncidenceData = prevData;

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        if(newValue && newValue?.name && prevData?.length > 0){
          prevData.find((item) =>
            item._id === newValue.sectionId).questions.find((q) =>
            q._id === newValue.questionId).issues.find((issue) =>
            issue._id === newValue.issue).name = newValue.name;
        }

        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onSuccess: () => {
        // setDataSections(updateSection({sectionId: data?._id, dataSections, section: data}));
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevIncidenceData;
        });
        online && putErrorCode(e);
      }
    });

  const {mutateAsync: solveInPlace, isLoading: isSolvingInPlace} = useMutation(AuditorAuditsApiService.solvedInPlace,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        prevIncidenceData = prevData;

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        if(newValue && prevData?.length > 0){
          prevData.find((item) =>
            item._id === newValue.sectionId).questions.find((q) =>
            q._id === newValue.questionId).issues.find((issue) =>
            issue._id === newValue.issue).solvedInPlace = newValue.solvedInPlace;
        }

        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onSuccess: () => {
        // setDataSections(updateSection({sectionId: data?._id, dataSections, section: data}));
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevIncidenceData;
        });
        online && putErrorCode(e);
      }
    });

  const {mutateAsync: updateDescription, isLoading: isUpdatingDescription} = useMutation(AuditorAuditsApiService.updateDescription,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        prevIncidenceData = prevData;

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        if(newValue && prevData?.length > 0){
          prevData.find((item) =>
            item._id === newValue.sectionId).questions.find((q) =>
            q._id === newValue.questionId).issues.find((issue) =>
            issue._id === newValue.issue).description = newValue.description;
        }

        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onSuccess: () => {
        // setDataSections(updateSection({sectionId: data?._id, dataSections, section: data}));
      },
      onError: (e) => {
        online && putErrorCode(e);
      }
    });

  const {mutateAsync: updateTaskName, isLoading: isUpdatingTaskName} = useMutation(AuditorAuditsApiService.updateTaskName,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        prevIncidenceData = prevData;

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        if(newValue && prevData?.length > 0){
          prevData.find((item) =>
            item._id === newValue.sectionId).questions.find((q) =>
            q._id === newValue.questionId).issues.find((issue) =>
            issue._id === newValue.issue).tasks.find((task) =>
            task._id === newValue.task).name = newValue.name;
        }

        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onSuccess: () => {
        // setDataSections(updateSection({sectionId: data?._id, dataSections, section: data}));
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevIncidenceData;
        });
        online && putErrorCode(e);
      }
    });


  useEffect(() => setSaving(isCreating), [isCreating, setSaving]);

  return {
    create,
    updateName,
    updateDescription,
    isCreating,
    isUpdatingName,
    isUpdatingDescription,
    updateTaskName,
    isUpdatingTaskName,
    isSolvingInPlace,
    solveInPlace,
    isLoading: isCreating || isUpdatingName || isUpdatingDescription || isUpdatingTaskName || isSolvingInPlace
  };
};

export const useCrudEvidencesRunAudit = () => {
  const queryClient = useQueryClient();
  const {setDataSections, setSaving} = useAuditData();
  const {putErrorCode} = useAlerts();
  const {id: auditId} = useParams();
  const {online} = useRunAuditOnlineStatus();
  const {setTimeElapsed} = useAuditCalc();
  let prevEvidenceData;

  const {mutateAsync: create, isLoading: isCreating} = useMutation(AuditorAuditsApiService.manageEvidence,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, auditId]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, auditId]);
        prevEvidenceData = prevData;

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        if (newValue && prevData?.length > 0) {
          const newData = prevData.map((item) => {
            if (item._id === newValue.sectionId) {
              return {
                ...item,
                questions: item.questions.map((q) => {
                  if (q._id === newValue.questionId) {
                    return {
                      ...q,
                      issues: q.issues.map((issue) => {
                        if (issue._id === newValue.issue) {
                          return { ...issue, evidences: newValue.evidences };
                        }
                        return issue;
                      })
                    };
                  }
                  return q;
                })
              };
            }
            return item;
          });
          queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, auditId], newData);
        }

        return { prevData };
      },
      onSuccess: (id, { sectionId, questionId, evidences, issue }) => {
        // Get fresh data from react-query cache instead of using potentially stale context data
        const freshData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, auditId]);
        if (freshData) {
          const updatedData = updateIssuesAndEvidences({ dataSections: freshData, sectionId, questionId, issue, evidences });
          // Filter only QUESTION sections for the context to avoid duplicating DATA and CONTACTS sections
          setDataSections(updatedData?.filter((x) => x.type === TYPE_SECTION.QUESTION));
          // Also update the cache to keep it in sync
          queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, auditId], updatedData);
        }
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, auditId], () => {
          return prevEvidenceData;
        });
        online && putErrorCode(e);
      }
    });

  useEffect(() => setSaving(isCreating), [isCreating, setSaving]);

  return {
    create,
    isCreating,
    isLoading: isCreating
  };
};

export const useCrudNote = () => {
  const queryClient = useQueryClient();
  const { setSaving} = useAuditData();
  const {putErrorCode} = useAlerts();
  const {id} = useParams();
  const {online} = useRunAuditOnlineStatus();
  const {setTimeElapsed} = useAuditCalc();
  let prevNoteData;

  const {mutateAsync: createNote, isLoading: isCreating} = useMutation(AuditorAuditsApiService.manageNote,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        prevNoteData = prevData;

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        if (newValue && prevData?.length > 0) {
          const newData = prevData.map((item) => {
            if (item._id === newValue.sectionId) {
              return {
                ...item,
                questions: item.questions.map((q) => {
                  if (q._id === newValue.questionId) {
                    return { ...q, note: newValue.note ? newValue.note : '' };
                  }
                  return q;
                })
              };
            }
            return item;
          });
          queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], newData);
        }
        return { prevData };
      },
      onSuccess: () => {
        // queryClient.invalidateQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        // queryClient.invalidateQueries(CACHE_KEY_LIST_AUDITOR_AUDITS);
        // queryClient.invalidateQueries(CACHE_KEY_AUDITS);
        // queryClient.invalidateQueries([CACHE_KEY_AUDITS, id]);
        // queryClient.invalidateQueries(['audit-sections',id]);

        // setDataSections(updateIssuesAndEvidences({sectionId, questionId, issue, evidences, dataSections}));
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevNoteData;
        });
        online && putErrorCode(e);
      }
    });

  useEffect(() => setSaving(isCreating), [isCreating, setSaving]);

  return {
    createNote,
    isCreating,
    isLoading: isCreating
  };
};

export const useCrudImage = () => {
  const queryClient = useQueryClient();
  const {setDataSections, setSaving} = useAuditData();
  const {putErrorCode} = useAlerts();
  const {online} = useRunAuditOnlineStatus();
  const {id} = useParams();
  const {setTimeElapsed} = useAuditCalc();
  let prevImageData;

  const {mutateAsync: upload, isLoading: isCreating} = useMutation(AuditorAuditsApiService.manageImages,
    {
      // eslint-disable-next-line no-unused-vars
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);

        const prevData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        prevImageData = prevData;
        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);
        if (newValue && prevData?.length > 0) {
          const newData = prevData.map((item) => {
            if (item._id === newValue.sectionId) {
              return {
                ...item,
                questions: item.questions.map((q) => {
                  if (q._id === newValue.questionId) {
                    return { ...q, noteImages: newValue?.noteImages };
                  }
                  return q;
                })
              };
            }
            return item;
          });
          queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], newData);
        }
        return { prevData };
      },
      onSuccess: (idRes, { sectionId, questionId, noteImages }) => {
        // Get fresh data from react-query cache instead of using potentially stale context data
        const freshData = queryClient.getQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id]);
        if (freshData) {
          const updatedData = updateNoteImages({
            dataSections: freshData,
            sectionId,
            questionId,
            noteImages
          });
          // Filter only QUESTION sections for the context to avoid duplicating DATA and CONTACTS sections
          setDataSections(updatedData?.filter((x) => x.type === TYPE_SECTION.QUESTION));
          // Also update the cache to keep it in sync
          queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], updatedData);
        }
      },
      onError: (e) => {
        queryClient.setQueryData([CACHE_KEY_LIST_AUDITOR_AUDITS_SECTIONS, id], () => {
          return prevImageData;
        });
        online && putErrorCode(e);
      }
    });

  useEffect(() => setSaving(isCreating), [isCreating, setSaving]);

  return {
    upload,
    isCreating,
    isLoading: isCreating
  };
};

export const useFinishAudit = () => {
  const queryClient = useQueryClient();
  const { online } = useRunAuditOnlineStatus();
  const {putErrorCode} = useAlerts();
  const {push} = useHistory();

  const {mutateAsync: onFinish, isLoading: isFinished} = useMutation(AuditorAuditsApiService.finishAudit,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_AUDITOR_AUDITS);
        push('/auditor/audits');
      },
      onError: (e) => {
        online && putErrorCode(e);
      }
    });

  return {
    onFinish,
    isFinished,
    isLoading: isFinished
  };
};

export const useExecuteAudit = () => {
  const queryClient = useQueryClient();
  const {online} = useRunAuditOnlineStatus();
  const {putErrorCode} = useAlerts();

  const {mutateAsync: onExecute, isLoading} = useMutation(AuditorAuditsApiService.executeAudit,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_AUDITOR_AUDITS);
      },
      onError: (e) => {
        online && putErrorCode(e);
      }
    });

  return {
    onExecute,
    isLoading
  };
};

export const useUpdateAudit = () => {
  const queryClient = useQueryClient();
  const {online} = useRunAuditOnlineStatus();
  const {setTimeElapsed} = useAuditCalc();
  const { id } = useParams();

  const {putErrorCode} = useAlerts();

  const {mutateAsync: onUpdate, isLoading} = useMutation(AuditorAuditsApiService.changeDateInRunning,
    {
      onMutate: async newValue => {
        await queryClient.cancelQueries([CACHE_KEY_AUDITS, id]);

        const elapsedTime = timeElapsedFunction(moment());
        setTimeElapsed(elapsedTime);

        const prevData = queryClient.getQueryData([CACHE_KEY_AUDITS, id]);
        if(newValue && newValue?.date && prevData?.data?.date){
          prevData.data.date = newValue.date.map((v) => v.toISOString());
        }
        queryClient.setQueryData([CACHE_KEY_AUDITS, id], () => {
          return prevData;
        });
        return {prevData};
      },
      onSuccess: () => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_AUDITOR_AUDITS);
      },
      onError: (e) => {
        online && putErrorCode(e);
      }
    });

  return {
    onUpdate,
    isLoading
  };
};
